import { useState, useEffect, useCallback, useRef } from "react";
import { BRAND } from "@/lib/brand-config";

/**
 * The brand compass artwork on its own — the gold compass rose used by both the
 * one-time marketing intro (`CompassIntro`) and the calm loading visual
 * (`CompassLoader`). Pass through `className` so callers can drive whichever
 * animation state they need (fast spin, settle, calm loop, static).
 */
export function CompassMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 200 200"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <circle cx="100" cy="100" r="90" stroke="var(--brand-gold)" strokeWidth="1.5" opacity="0.6"/>
      <circle cx="100" cy="100" r="70" stroke="var(--brand-gold)" strokeWidth="0.8" opacity="0.3"/>
      <circle cx="100" cy="100" r="50" stroke="var(--brand-gold)" strokeWidth="0.5" opacity="0.2" strokeDasharray="4 4"/>

      <line x1="100" y1="12" x2="100" y2="28" stroke="var(--brand-gold)" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
      <line x1="100" y1="172" x2="100" y2="188" stroke="var(--brand-gold)" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
      <line x1="12" y1="100" x2="28" y2="100" stroke="var(--brand-gold)" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>
      <line x1="172" y1="100" x2="188" y2="100" stroke="var(--brand-gold)" strokeWidth="2" strokeLinecap="round" opacity="0.8"/>

      <text x="100" y="9" textAnchor="middle" fill="var(--brand-gold)" fontSize="7" fontFamily="Lora, Georgia, serif" fontWeight="600" opacity="0.7">N</text>
      <text x="100" y="198" textAnchor="middle" fill="var(--brand-gold)" fontSize="7" fontFamily="Lora, Georgia, serif" fontWeight="600" opacity="0.5">S</text>
      <text x="196" y="103" textAnchor="middle" fill="var(--brand-gold)" fontSize="7" fontFamily="Lora, Georgia, serif" fontWeight="600" opacity="0.5">E</text>
      <text x="5" y="103" textAnchor="middle" fill="var(--brand-gold)" fontSize="7" fontFamily="Lora, Georgia, serif" fontWeight="600" opacity="0.5">W</text>

      <line x1="25" y1="25" x2="33" y2="33" stroke="var(--brand-gold)" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      <line x1="175" y1="25" x2="167" y2="33" stroke="var(--brand-gold)" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      <line x1="25" y1="175" x2="33" y2="167" stroke="var(--brand-gold)" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>
      <line x1="175" y1="175" x2="167" y2="167" stroke="var(--brand-gold)" strokeWidth="1" strokeLinecap="round" opacity="0.3"/>

      <path d="M100 28 L118 100 L100 172 L82 100 Z" fill="none" stroke="var(--brand-gold)" strokeWidth="1" opacity="0.3"/>
      <path d="M100 28 L118 100 L100 78 Z" fill="var(--brand-gold)" opacity="0.9"/>
      <path d="M100 28 L82 100 L100 78 Z" fill="var(--brand-gold)" opacity="0.4"/>
      <path d="M100 172 L118 100 L100 122 Z" fill="var(--brand-gold)" opacity="0.15"/>
      <path d="M100 172 L82 100 L100 122 Z" fill="var(--brand-gold)" opacity="0.35"/>

      <circle cx="100" cy="100" r="5" fill="var(--brand-gold)" opacity="0.8"/>
      <circle cx="100" cy="100" r="2.5" fill="var(--brand-dark-bg)"/>
    </svg>
  );
}

export function CompassIntro({ onComplete }: { onComplete: () => void }) {
  const [phase, setPhase] = useState<"spinning" | "locking" | "unlocking" | "done">("spinning");
  const dismissedRef = useRef(false);

  const dismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    setPhase("done");
    onComplete();
  }, [onComplete]);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("locking"), 450);
    const t2 = setTimeout(() => setPhase("unlocking"), 800);
    const t3 = setTimeout(dismiss, 1200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [dismiss]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Enter" || e.key === " ") dismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dismiss]);

  if (phase === "done") return null;

  return (
    <div
      className={`compass-intro-overlay ${phase === "unlocking" ? "compass-intro-reveal" : ""}`}
      aria-hidden="true"
      onClick={dismiss}
      data-testid="compass-intro-overlay"
      style={{ cursor: "pointer" }}
    >
      <div className={`compass-intro-inner ${phase === "locking" ? "compass-lock" : ""} ${phase === "unlocking" ? "compass-unlock" : ""}`}>
        <CompassMark
          className={`compass-intro-svg ${phase === "spinning" ? "compass-spin-fast" : ""} ${phase === "locking" ? "compass-spin-settle" : ""}`}
        />

        <div className={`compass-intro-ring ${phase !== "spinning" ? "compass-ring-visible" : ""}`} />
      </div>

      <div className={`compass-intro-text ${phase === "locking" ? "compass-text-visible" : ""} ${phase === "unlocking" ? "compass-text-exit" : ""}`}>
        <span>{BRAND.domain}</span>
      </div>
    </div>
  );
}
